/**
 * Pure diff/summary logic for the version-history drawer.
 *
 * Blocks pair by (category, ordinal) identity — the same rule as
 * focusTargetAcrossSnapshots (composedPromptTargets.ts): ordinal counts
 * same-category blocks EARLIER in the array. Index-based pairing would
 * misreport every diff that inserts, removes, or moves a block.
 */
import type { PersonaBundleBlock, PersonaBundleResponse } from '@/types/personaWizard'
import { blockFieldLabel } from './blockTaxonomy'

export interface BlockChange {
  category: string
  ordinal: number
  before?: string
  after?: string
}

export interface BundleDiff {
  blocks: {
    added: BlockChange[]
    removed: BlockChange[]
    changed: BlockChange[]
    reorderedOnly: boolean
  }
  skills: { added: string[]; removed: string[] }
  otherChanged: string[]
}

interface IdentityBlock {
  category: string
  ordinal: number
  content: string
}

function withIdentity(blocks: PersonaBundleBlock[]): IdentityBlock[] {
  const counts = new Map<string, number>()
  return blocks.map((b) => {
    const ordinal = counts.get(b.category) ?? 0
    counts.set(b.category, ordinal + 1)
    return { category: b.category, ordinal, content: b.content }
  })
}

/** JSON-stringify with recursively sorted object keys, so key order never counts as a change. */
function sortedStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(sortedStringify).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${sortedStringify(v)}`)
    return `{${entries.join(',')}}`
  }
  return JSON.stringify(value)
}

const deepEqual = (a: unknown, b: unknown): boolean =>
  sortedStringify(a) === sortedStringify(b)

const OTHER_FIELDS = [
  ['tool_bindings', 'tool bindings'],
  ['workflow_bindings', 'workflow bindings'],
  ['channel_overrides', 'channel overrides'],
  ['policy', 'policy'],
  ['variable_values', 'variables'],
] as const

const identityKey = (b: { category: string; ordinal: number }) =>
  `${b.category}:${b.ordinal}`

export function diffBundles(
  a: PersonaBundleResponse,
  b: PersonaBundleResponse,
): BundleDiff {
  const aBlocks = withIdentity(a.blocks)
  const bBlocks = withIdentity(b.blocks)
  const aByKey = new Map(aBlocks.map((x) => [identityKey(x), x]))
  const bByKey = new Map(bBlocks.map((x) => [identityKey(x), x]))

  const added: BlockChange[] = []
  const removed: BlockChange[] = []
  const changed: BlockChange[] = []
  for (const blk of bBlocks) {
    const prev = aByKey.get(identityKey(blk))
    if (!prev) added.push({ category: blk.category, ordinal: blk.ordinal, after: blk.content })
    else if (prev.content !== blk.content)
      changed.push({ category: blk.category, ordinal: blk.ordinal, before: prev.content, after: blk.content })
  }
  for (const blk of aBlocks) {
    if (!bByKey.has(identityKey(blk)))
      removed.push({ category: blk.category, ordinal: blk.ordinal, before: blk.content })
  }
  const sequence = (blocks: PersonaBundleBlock[]) =>
    JSON.stringify(blocks.map((x) => [x.category, x.content]))
  const reorderedOnly =
    added.length === 0 &&
    removed.length === 0 &&
    changed.length === 0 &&
    sequence(a.blocks) !== sequence(b.blocks)

  const unionNames = (x: PersonaBundleResponse) =>
    new Set([...x.default_skills, ...x.auto_skills].map((s) => s.name))
  const aNames = unionNames(a)
  const bNames = unionNames(b)
  const skillsAdded = [...bNames].filter((n) => !aNames.has(n))
  const skillsRemoved = [...aNames].filter((n) => !bNames.has(n))

  const otherChanged: string[] = []
  for (const [field, label] of OTHER_FIELDS) {
    if (!deepEqual(a[field], b[field])) otherChanged.push(label)
  }
  // A default↔auto move keeps the union identical but changes per-list
  // membership — report it as configuration, not a false add/remove pair.
  const names = (skills: { name: string }[]) => skills.map((s) => s.name).sort()
  const movedBetweenLists =
    skillsAdded.length === 0 &&
    skillsRemoved.length === 0 &&
    (!deepEqual(names(a.default_skills), names(b.default_skills)) ||
      !deepEqual(names(a.auto_skills), names(b.auto_skills)))
  if (movedBetweenLists) otherChanged.push('skills configuration')

  return {
    blocks: { added, removed, changed, reorderedOnly },
    skills: { added: skillsAdded, removed: skillsRemoved },
    otherChanged,
  }
}

export function isEmptyDiff(diff: BundleDiff): boolean {
  return (
    diff.blocks.added.length === 0 &&
    diff.blocks.removed.length === 0 &&
    diff.blocks.changed.length === 0 &&
    !diff.blocks.reorderedOnly &&
    diff.skills.added.length === 0 &&
    diff.skills.removed.length === 0 &&
    diff.otherChanged.length === 0
  )
}

const SUMMARY_MAX_PARTS = 4

/**
 * Human summary of `next` relative to its predecessor, for history rows:
 * "edited Personality traits, added skill send-brochure". `prev` is
 * undefined for v1 → "Created".
 */
export function summarizeChange(
  prev: PersonaBundleResponse | undefined,
  next: PersonaBundleResponse,
): string {
  if (!prev) return 'Created'
  const diff = diffBundles(prev, next)
  const parts: string[] = []
  for (const c of diff.blocks.changed) parts.push(`edited ${blockFieldLabel(c.category, c.ordinal)}`)
  for (const c of diff.blocks.added) parts.push(`added ${blockFieldLabel(c.category, c.ordinal)}`)
  for (const c of diff.blocks.removed) parts.push(`removed ${blockFieldLabel(c.category, c.ordinal)}`)
  for (const s of diff.skills.added) parts.push(`added skill ${s}`)
  for (const s of diff.skills.removed) parts.push(`removed skill ${s}`)
  for (const f of diff.otherChanged) parts.push(`changed ${f}`)
  if (parts.length === 0) {
    return diff.blocks.reorderedOnly ? 'reordered blocks' : 'no content changes'
  }
  if (parts.length > SUMMARY_MAX_PARTS) {
    const extra = parts.length - SUMMARY_MAX_PARTS
    return `${parts.slice(0, SUMMARY_MAX_PARTS).join(', ')}, +${extra} more`
  }
  return parts.join(', ')
}
